import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ComponentChanges, FormModelControl } from '@bcl/common-ui';
import { SnackbarService } from '@bcl/design-system';
import { RequiredClaimsService } from '@ews/users-management';
import {
  BehaviorSubject,
  combineLatest,
  ReplaySubject,
  Subject,
  takeUntil,
} from 'rxjs';
import { Claims } from 'src/app/authorization';
import {
  NC,
  NCList,
  NCsListFilter,
  NCsPermissions,
  NonconformitiesListPageChanged,
  ParentContext,
} from './nonconformities-api.model';
import { NCsApiService } from './nonconformities-api.service';
import { Router } from '@angular/router';
import { Action } from 'src/app/pages/action/action.model';
import { NonconformityDialogComponent } from './nonconformity-dialog/nonconformity-dialog.component';
import { Nonconformity } from 'src/app/pages/nonconformity/nonconformity.model';
import { NonconformityApiService } from 'src/app/pages/nonconformity/nonconformity.service';
import { AccordionService } from 'src/app/services/accordion.service';
@Component({
  selector: 'app-nonconformities-list',
  templateUrl: './nonconformities-list.component.html',
  styleUrls: ['./nonconformities-list.component.scss'],
})
export class NonconformitiesListComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input()
  public titleLabel?: string;
  @Input()
  public ncsList: NCList = { elements: [], totalEntries: 0 };
  @Input()
  public canAdd = false;
  @Input()
  public canEdit = false;
  @Input()
  public canDelete = false;
  @Input()
  public pageSize = 5;
  @Input()
  public showItemList = true;
  @Input()
  public parentContext!: ParentContext;
  @Input()
  public customerId?: string;
  @Input()
  public locationId?: string;
  @Input()
  public backUrl = '';
  @Input()
  public visit?: any; // Add visit input
  @Input()
  public isEditingDisabled = false;
  @Input()
  public openByDefault = false;

  @Output()
  public loadMore = new EventEmitter<NonconformitiesListPageChanged>();
  @Output()
  public toggleVisibility = new EventEmitter<void>();

  public readonly ncs$ = new BehaviorSubject<NC[]>([]);

  public permissions$ = new ReplaySubject<NCsPermissions>(1);

  public readonly filterForm: FormGroup<FormModelControl<NCsListFilter>>;

  private currentPage = 1;
  private lastPage = 0;
  private destroyed$ = new Subject<void>();
  constructor(
    public dialog: MatDialog,
    requiredClaimsService: RequiredClaimsService,
    private ncsService: NCsApiService,
    fb: FormBuilder,
    public snackbarService: SnackbarService,
    private router: Router,
    private nonconformityService: NonconformityApiService,
    private accordionService: AccordionService
  ) {
    this.filterForm = fb.group({});

    this.filterForm.valueChanges.subscribe((x) => {
      this.currentPage = 1;
      this.loadMore.next({
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        filter: { ...x },
      });
    });
    combineLatest([
      requiredClaimsService.hasAccess(Claims.CreateNC),
      requiredClaimsService.hasAccess(Claims.UpdateNC),
      requiredClaimsService.hasAccess(Claims.DeleteNC),
    ]).subscribe(([canAdd, canEdit, canDelete]) => {
      this.canAdd = canAdd;
      this.canEdit = canEdit;
      this.canDelete = canDelete;
    });
  }

  // Computed property to check if form should be disabled
  get isFormDisabled(): boolean {
    return this.isEditingDisabled;
  }
  public get hasNextPage(): boolean {
    return this.currentPage < this.lastPage;
  }
  public ngOnInit(): void {
    this.loadMore.emit({
      pageNumber: 1,
      pageSize: this.pageSize,
      filter: {
        date: '0',
      },
    });

    // Open the accordion by default if specified
    if (this.openByDefault) {
      this.accordionService.openComponent('ncs');
    }

    // Subscribe to accordion service to manage showItemList state
    this.accordionService
      .getCurrentOpenComponent()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((openComponentId) => {
        this.showItemList = openComponentId === 'ncs';
      });
  }
  public ngOnChanges(
    changes: ComponentChanges<NonconformitiesListComponent>
  ): void {
    if (changes.parentContext?.currentValue) {
      this.ncsService.setParentContext(changes.parentContext.currentValue);
    }
    const ncsList = changes.ncsList?.currentValue;
    if (ncsList) {
      this.ncs$.next([
        ...(this.currentPage > 1 ? this.ncs$.value : []),
        ...ncsList.elements,
      ]);
      this.lastPage = Math.ceil(ncsList.totalEntries / this.pageSize);
    }
  }
  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
  showItems() {
    // Use accordion service to manage state
    if (this.showItemList) {
      this.accordionService.closeComponent('ncs');
    } else {
      this.accordionService.openComponent('ncs');
    }
  }

  public loadMoreNCs(): void {
    this.currentPage++;
    this.loadMore.next({
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      filter: { ...this.filterForm.value },
    });
  }

  public editNC(nonconformity?: NC): void {
    this.router.navigate(['nonconformity'], {
      state: {
        backUrl: this.backUrl,
        parentObject: this.parentContext.parentObject,
        parentId: this.parentContext.parentId,
        customerId: this.customerId,
        locationId: this.locationId,
        nonconformityId: nonconformity?.id ?? 0,
      },
    });
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public showNonconformityDialog(type: string, nonconformity: NC | null): void {
    const dialogRef = this.dialog.open(NonconformityDialogComponent, {
      disableClose: true,
      data: { nonconformity, nonconformityType: type },
    });

    dialogRef.afterClosed().subscribe(
      (
        result:
          | {
              nonconformityType: 'ADD' | 'UPDATE' | 'DELETE';
              nonconformity: Nonconformity;
            }
          | false
      ) => {
        if (result) {
          switch (result.nonconformityType) {
            case 'DELETE':
              this.nonconformityService
                .deleteNonconformity(nonconformity.id)
                .subscribe(() => {
                  this.loadMore.emit({
                    pageNumber: this.currentPage,
                    pageSize: this.pageSize,
                    filter: { ...this.filterForm.value },
                  });
                  this.snackbarService.open(
                    'green',
                    'NCS.LIST.MESSAGES.NONCONFORMITY_DELETED_SUCCESSFULLY'
                  );
                });
              break;
          }
        }
      }
    );
  }
}
